// Settings: each field PATCHes only itself (PATCH /platform/leagues/:id with
// one key) and shows its own Saved / error state, so one field's save can
// never overwrite another's (#336). Subdomain and custom domain moved to
// Domains; "active" became Suspend in Danger zone.

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '@/api'
import { Card, CardHeader, EditableSelectField, EditableTextField, KeyValueList } from '@/components/ui'
import { formatDate, titleCase } from '@/lib/format'
import { useTenant } from '../tenantContext'

const URL_RE = /^https?:\/\/\S+\.\S+$/

export default function SettingsTab() {
  const { tenant, apply } = useTenant()
  const sports = tenant.sports

  // Only enabled packs can be the default (the server refuses others with 409).
  const sportOptions = sports
    .filter((s) => s.is_enabled || s.sport_id === tenant.default_sport_id)
    .map((s) => ({ value: s.sport_id, label: s.display_name || titleCase(s.sport_id), disabled: !s.is_enabled }))
  if (!sportOptions.some((o) => o.value === tenant.default_sport_id)) {
    sportOptions.unshift({ value: tenant.default_sport_id, label: titleCase(tenant.default_sport_id), disabled: false })
  }

  return (
    <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
      <Card>
        <CardHeader title="League" description="Each field saves on its own when you leave it. Escape undoes an edit." />
        <div className="grid grid-cols-1 gap-5">
          <EditableTextField
            label="Name"
            value={tenant.name}
            maxLength={120}
            validate={(v) => (v ? null : 'Name is required.')}
            onSave={async (name) => apply(await api.tenants.update(tenant.id, { name }))}
          />
          <EditableTextField
            label="Description"
            multiline
            maxLength={2000}
            placeholder="A sentence or two about the league. Shown on the public site."
            hint="Cmd/Ctrl + Enter or click away to save."
            value={tenant.description ?? ''}
            onSave={async (description) => apply(await api.tenants.update(tenant.id, { description: description || null }))}
          />
          <EditableSelectField
            label="Default sport"
            hint={
              <>
                Used when no season is in scope. Only enabled sport packs can be the default; enable more on the{' '}
                <Link to="../sports" className="text-accent hover:underline">
                  Sports
                </Link>{' '}
                tab.
              </>
            }
            value={tenant.default_sport_id}
            options={sportOptions}
            onSave={async (default_sport_id) => apply(await api.tenants.update(tenant.id, { default_sport_id }))}
          />
          <LogoField tenantLogo={tenant.logo_url} onSave={async (logo_url) => apply(await api.tenants.update(tenant.id, { logo_url }))} />
        </div>
      </Card>

      <div className="grid min-w-0 content-start gap-4">
        <Card>
          <CardHeader title="Record" />
          <KeyValueList
            items={[
              { label: 'Tenant id', value: <span className="font-mono">{tenant.id}</span> },
              { label: 'Created', value: formatDate(tenant.created_at) },
              { label: 'Last updated', value: formatDate(tenant.updated_at) },
            ]}
          />
        </Card>
        <Card>
          <CardHeader title="Moved" />
          <ul className="grid gap-2 text-[13px] text-muted">
            <li>
              Subdomain, custom domain and the redirect live on{' '}
              <Link to="../domains" className="font-semibold text-accent hover:underline">
                Domains
              </Link>
              .
            </li>
            <li>
              Taking a league offline is{' '}
              <Link to="../danger-zone" className="font-semibold text-accent hover:underline">
                Suspend
              </Link>{' '}
              in Danger zone, with a reason.
            </li>
            <li>
              Paid features are on{' '}
              <Link to="../add-ons" className="font-semibold text-accent hover:underline">
                Add-ons
              </Link>
              .
            </li>
          </ul>
        </Card>
      </div>
    </div>
  )
}

function LogoField({ tenantLogo, onSave }: { tenantLogo: string | null; onSave: (url: string | null) => Promise<unknown> }) {
  const [broken, setBroken] = useState<string | null>(null)
  return (
    <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start">
      <EditableTextField
        label="Logo URL"
        mono
        placeholder="https://…/logo.png"
        hint="An https link to the league's logo. Leave empty to remove it."
        value={tenantLogo ?? ''}
        validate={(v) => (!v || URL_RE.test(v) ? null : 'Enter a full http(s) URL.')}
        onSave={(v) => onSave(v || null)}
      />
      <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-md border border-line bg-raised sm:mt-6" aria-label="Logo preview">
        {tenantLogo && broken !== tenantLogo ? (
          <img src={tenantLogo} alt="Current logo" className="max-h-full max-w-full object-contain" onError={() => setBroken(tenantLogo)} />
        ) : (
          <span className="px-1 text-center text-2xs text-subtle">{tenantLogo ? "Can't load" : 'No logo'}</span>
        )}
      </div>
    </div>
  )
}
